/**
 * Fields without column explosion (SPEC §5.3).
 */
import type { FieldDef, FieldType, ItemSnapshot, Money, Place } from './types'

export const SEED_FIELDS: FieldDef[] = [
  { key: 'amount', type: 'money', label: 'Amount' },
  { key: 'vendor', type: 'text', label: 'Vendor' },
  { key: 'permit_no', type: 'text', label: 'Permit #' },
  { key: 'address', type: 'place', label: 'Address' },
  { key: 'url', type: 'url', label: 'URL' },
  { key: 'booking_ref', type: 'text', label: 'Booking ref' },
  { key: 'phone', type: 'text', label: 'Phone' },
]

export const FIELD_TYPES: FieldType[] = ['text', 'number', 'money', 'date', 'url', 'select', 'place']

/** snake_case key from a human label; never creates near-duplicates of existing keys. */
export function fieldKeyFromLabel(label: string, existing: Iterable<string> = []): string {
  const base =
    label
      .trim()
      .toLowerCase()
      .replace(/#/g, 'no')
      .replace(/[^a-z0-9]+/g, '_')
      .replace(/^_+|_+$/g, '') || 'field'
  const taken = new Set(existing)
  if (!taken.has(base)) return base
  let i = 2
  while (taken.has(`${base}_${i}`)) i++
  return `${base}_${i}`
}

/** Find an existing field that is "the same" as a label (case/spacing/plural-insensitive). */
export function findSimilarField(label: string, fields: Iterable<FieldDef>): FieldDef | undefined {
  const norm = (s: string) =>
    s
      .toLowerCase()
      .replace(/[^a-z0-9]/g, '')
      .replace(/s$/, '')
  const n = norm(label)
  for (const f of fields) if (norm(f.key) === n || norm(f.label) === n) return f
  return undefined
}

export function hasValue(v: unknown): boolean {
  if (v === undefined || v === null || v === '') return false
  if (typeof v === 'object' && 'cents' in (v as object)) return Number.isFinite((v as Money).cents)
  if (typeof v === 'object' && 'name' in (v as object)) return !!(v as Place).name
  return true
}

/** Union of childFields declared by ancestors (nearest first). */
export function inheritedChildFields(ancestors: ItemSnapshot[]): string[] {
  const out: string[] = []
  for (const a of ancestors) for (const k of a.childFields ?? []) if (!out.includes(k)) out.push(k)
  return out
}

/**
 * Field rows to show on an item: those with a value, those an ancestor warrants via
 * childFields, and ad-hoc ones the user just added. Registry order for stability.
 */
export function visibleFieldKeys(
  item: ItemSnapshot,
  ancestors: ItemSnapshot[],
  registry: Map<string, FieldDef>,
  adHoc: Iterable<string> = [],
  /** Fields its siblings have values for: once one sibling gets an amount, the rest offer it too. */
  siblingKeys: Iterable<string> = [],
): string[] {
  const keys = new Set<string>()
  for (const [k, v] of Object.entries(item.props)) if (k !== 'view' && hasValue(v)) keys.add(k)
  for (const k of inheritedChildFields(ancestors)) keys.add(k)
  for (const k of adHoc) keys.add(k)
  for (const k of siblingKeys) keys.add(k)
  const order = [...registry.keys()]
  return [...keys].filter((k) => registry.has(k)).sort((a, b) => order.indexOf(a) - order.indexOf(b))
}

/**
 * A row's subtitle (the small line under it), as its card sets it: the nearest ancestor with a
 * `rowFields` list wins, and a tab can set one for a card (`overrides`, by card id). The list
 * holds field keys, `tag:<group>` (that group's values, or a plain tag when the item has it), and
 * `hide:date` / `hide:person` / `hide:repeat` for the things shown unless hidden. Unset = money.
 */
export interface Subtitle {
  /** Fields and tags, in order. */
  keys: string[]
  date: boolean
  person: boolean
  repeat: boolean
}

export const SUBTITLE_BUILTINS = ['date', 'person', 'repeat'] as const

/** The list that decides a row's subtitle (null = the default), from its ancestors (nearest first). */
export function subtitleList(ancestors: ItemSnapshot[], overrides?: Readonly<Record<string, string[]>>): string[] | null {
  // A tab can set one for all its cards (`*`): it wins over a card's own, not over the tab's for that card.
  const all = overrides?.[TAB_WIDE]
  for (const a of ancestors) {
    const o = overrides?.[a.id]
    if (o) return o
    if (!all && a.rowFields) return a.rowFields
  }
  return all ?? null
}

/** The key a tab's subtitle for all of its cards is kept under (in `SavedView.subtitles`). */
export const TAB_WIDE = '*'

export function subtitleFor(ancestors: ItemSnapshot[], registry: Map<string, FieldDef>, overrides?: Readonly<Record<string, string[]>>): Subtitle {
  const list = subtitleList(ancestors, overrides)
  if (!list) return { keys: [...registry.values()].filter((d) => d.type === 'money').map((d) => d.key), date: true, person: true, repeat: true }
  return {
    keys: list.filter((k) => registry.has(k) || (k.startsWith('tag:') && k.length > 4)),
    date: !list.includes('hide:date'),
    person: !list.includes('hide:person'),
    repeat: !list.includes('hide:repeat'),
  }
}

/** The fields and tags a row's subtitle shows (see `subtitleFor`). */
export function rowFieldKeys(ancestors: ItemSnapshot[], registry: Map<string, FieldDef>, overrides?: Readonly<Record<string, string[]>>): string[] {
  return subtitleFor(ancestors, registry, overrides).keys
}

/** Relabel resolution: the item itself, then nearest ancestor with a label for `key`. */
export function resolveLabel(key: string, self: ItemSnapshot | undefined, ancestors: ItemSnapshot[], fallback: string): string {
  if (self?.labels?.[key]) return self.labels[key]!
  for (const a of ancestors) if (a.labels?.[key]) return a.labels[key]!
  return fallback
}

/** Default attachment tag in a relabeled subtree: "Receipts" → "receipt". */
export function defaultAttachmentTag(label: string | undefined): string | undefined {
  if (!label) return undefined
  const l = label.trim().toLowerCase()
  if (!l || l === 'attachments' || l === 'files') return undefined
  const singular = l.endsWith('ies') ? l.slice(0, -3) + 'y' : l.endsWith('s') ? l.slice(0, -1) : l
  return singular.replace(/\s+/g, '-')
}

/**
 * "+ field" suggestions: fields used by siblings → used elsewhere under the same
 * top-level card → the rest of the registry. Existing keys on the item are excluded.
 */
export function suggestFields(item: ItemSnapshot, siblings: ItemSnapshot[], sameCard: ItemSnapshot[], registry: Map<string, FieldDef>, query = ''): FieldDef[] {
  const have = new Set(Object.keys(item.props).filter((k) => hasValue(item.props[k])))
  const score = new Map<string, number>()
  const bump = (k: string, s: number) => score.set(k, Math.max(score.get(k) ?? 0, s))
  for (const s of siblings) for (const [k, v] of Object.entries(s.props)) if (hasValue(v)) bump(k, 3)
  for (const s of sameCard) for (const [k, v] of Object.entries(s.props)) if (hasValue(v)) bump(k, 2)
  for (const k of registry.keys()) bump(k, 1)
  const q = query.trim().toLowerCase()
  return [...score.entries()]
    .filter(([k]) => registry.has(k) && !have.has(k) && k !== 'view')
    .map(([k, s]) => ({ def: registry.get(k)!, s }))
    .filter(({ def }) => !q || def.key.includes(q) || def.label.toLowerCase().includes(q))
    .sort((a, b) => b.s - a.s || a.def.label.localeCompare(b.def.label))
    .map(({ def }) => def)
}

// ---------------------------------------------------------------------------
// Value formatting / parsing

export function formatMoney(m: Money | undefined | null): string {
  if (!m || !Number.isFinite(m.cents)) return ''
  const neg = m.cents < 0
  const abs = Math.abs(m.cents)
  const dollars = Math.floor(abs / 100)
  const cents = abs % 100
  const s = `$${dollars.toLocaleString('en-US')}${cents ? '.' + String(cents).padStart(2, '0') : ''}`
  return neg ? `-${s}` : s
}

export function parseMoney(input: string): Money | null {
  const cleaned = input.replace(/[,$\s]/g, '')
  if (!cleaned) return null
  if (!/^-?\d*(\.\d{0,2})?$/.test(cleaned) || cleaned === '-' || cleaned === '.') return null
  const cents = Math.round(parseFloat(cleaned) * 100)
  return Number.isFinite(cents) ? { cents, currency: 'USD' } : null
}

export function formatFieldValue(def: FieldDef | undefined, v: unknown): string {
  if (!hasValue(v)) return ''
  switch (def?.type) {
    case 'money':
      return formatMoney(v as Money)
    case 'place': {
      const p = v as Place
      return p.address ? `${p.name} — ${p.address}` : p.name
    }
    case 'number':
      return typeof v === 'number' ? v.toLocaleString('en-US') : String(v)
    default:
      if (typeof v === 'object') {
        if (v && 'cents' in v) return formatMoney(v as Money)
        if (v && 'name' in v) return (v as Place).name
        return JSON.stringify(v)
      }
      return String(v)
  }
}

/** Plain text of a field value (search / FTS). */
export function fieldValueText(v: unknown): string {
  if (!hasValue(v)) return ''
  if (typeof v === 'object') {
    if (v && 'cents' in v) return formatMoney(v as Money)
    if (v && 'name' in v) {
      const p = v as Place
      return [p.name, p.address].filter(Boolean).join(' ')
    }
    return ''
  }
  return String(v)
}

/** Coerce loose input (MCP/agents) into a typed value for a field. */
export function coerceFieldValue(def: FieldDef, v: unknown): unknown {
  if (v === null || v === undefined || v === '') return null
  switch (def.type) {
    case 'money':
      if (typeof v === 'number') return { cents: Math.round(v * 100), currency: 'USD' }
      if (typeof v === 'string') return parseMoney(v)
      if (typeof v === 'object' && v && 'cents' in v) return { cents: Math.round(Number((v as Money).cents)), currency: 'USD' }
      return null
    case 'number':
      return typeof v === 'number' ? v : Number.isFinite(Number(v)) ? Number(v) : null
    case 'place':
      if (typeof v === 'string') return { name: v }
      if (typeof v === 'object' && v && 'name' in v) {
        const p = v as Place
        const out: Place = { name: String(p.name) }
        if (p.address) out.address = String(p.address)
        if (typeof p.lat === 'number') out.lat = p.lat
        if (typeof p.lng === 'number') out.lng = p.lng
        if (p.mapsUrl) out.mapsUrl = String(p.mapsUrl)
        return out
      }
      return null
    case 'select':
      return String(v)
    default:
      return typeof v === 'string' ? v : String(v)
  }
}

/** Sum of `amount` over items (Phase 4 rollups; used by the item page already). */
export function sumAmounts(items: ItemSnapshot[], key = 'amount'): Money | null {
  let cents = 0
  let any = false
  for (const i of items) {
    const v = i.props[key] as Money | undefined
    if (v && typeof v === 'object' && Number.isFinite(v.cents)) {
      cents += v.cents
      any = true
    }
  }
  return any ? { cents, currency: 'USD' } : null
}
